import {
  DEFAULT_GRID_COLUMNS,
  diffMoves,
  resizeItem,
  type GridLayoutItem,
  type WidgetInstance,
  type WidgetMove,
  type WidgetRegistry,
} from "@salestrak/ui";

import { managedSeed, type ManagedWidget } from "#app/widgets/dashboards/managed-widgets";

/**
 * Pure helpers for MANAGED surfaces (My Work, Team Overview): the half/full
 * width rhythm their catalogs are designed around, and the virtual seeds a
 * non-editor sees on a never-seeded board. Pure so the node test harness can
 * truth-table the snapping math.
 */

// Must match WidgetGrid's default; dashboard grids never pass a columns prop.
// Derived from the library grid default so half/full width stay in lockstep
// with the grid's real column count (D12).
export const FULL_WIDTH = DEFAULT_GRID_COLUMNS;
export const HALF_WIDTH = DEFAULT_GRID_COLUMNS / 2;
export const HALF_FULL_WIDTH_OPTIONS = [
  { value: HALF_WIDTH, label: "Half width" },
  { value: FULL_WIDTH, label: "Full width" },
];

/** A catalog designed entirely on the half/full rhythm opts its grid into
 *  width snapping + the Half/Full width menu. */
export function usesHalfFullWidths(catalog?: ManagedWidget[]): boolean {
  return Boolean(
    catalog?.length && catalog.every((widget) => widget.w === HALF_WIDTH || widget.w === FULL_WIDTH),
  );
}

/**
 * Re-settle any width-changing move onto the half/full rhythm a managed
 * catalog is designed around: width snaps to half or full (whichever is
 * nearer) and x snaps to the matching column, with siblings reflowed through
 * `resizeItem` so the commit stays collision-free.
 */
export function snapHalfFullMoves(instances: WidgetInstance[], moves: WidgetMove[]): WidgetMove[] {
  const previous = new Map(instances.map((item) => [item.id, item]));
  let next: GridLayoutItem[] = instances.map((item) => {
    const move = moves.find((candidate) => candidate.id === item.id);
    return move ? { ...item, ...move } : item;
  });

  for (const move of moves) {
    const prior = previous.get(move.id);
    if (!prior || move.w === prior.w) continue;
    const width = move.w <= (HALF_WIDTH + FULL_WIDTH) / 2 ? HALF_WIDTH : FULL_WIDTH;
    const x =
      width === FULL_WIDTH
        ? 0
        : prior.w === FULL_WIDTH
          ? move.x > 0
            ? HALF_WIDTH
            : 0
          : prior.x >= HALF_WIDTH
            ? HALF_WIDTH
            : 0;
    next = resizeItem(next, move.id, x, move.y, width, move.h, DEFAULT_GRID_COLUMNS, {
      minW: width,
      maxW: width,
    });
  }

  return diffMoves(instances, next);
}

/**
 * Viewer-friendly seeds (P3): a never-seeded managed surface shows the
 * default-on catalog to non-editors as VIRTUAL (non-persisted) instances
 * instead of a blank board. Personalize adopts whatever is displayed, so a
 * fork starts from these; editors never see them (the first-visit seeding
 * effect owns their empty board).
 */
export function managedVirtualInstances(
  catalog: ManagedWidget[],
  registry: WidgetRegistry,
): WidgetInstance[] {
  return catalog
    .filter((widget) => widget.defaultOn)
    .map((widget) => {
      const seed = managedSeed(widget);
      return {
        id: `virtual-${widget.widgetKey}`,
        type: seed.type,
        config: seed.config,
        x: seed.x,
        y: seed.y,
        w: seed.w,
        h: seed.h,
        schemaVersion: registry.get(seed.type)?.schemaVersion ?? 1,
      };
    });
}
